import type { OperationItem } from '@ops/entities/operation-item';
import { toUtcDate } from '@ops/entities/operation-item';
import { displayTitle } from '@ops/widgets/operations-layout/lib/display-title';
import { travellerCount } from '@travel-planner/shared/utils';
import { format } from 'date-fns';

export type DashboardOperatorView = {
  id: string;
  initials: string;
  name: string;
};

export type DashboardRowView = {
  id: string;
  dateLabel: string;
  timeLabel: string;
  service: string;
  category: string;
  itineraryId: string | null;
  itineraryRef: string;
  traveller: string;
  /** Itinerary owner (account), shown under the itinerary ref (XT-380 list). */
  owner: string;
  location: string;
  operators: DashboardOperatorView[];
  statusId: string | null;
  statusTitle: string;
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

const travellerLabel = (item: OperationItem): string => {
  const first = item.itineraryContactFirstName?.trim();
  const last = item.itineraryContactLastName?.trim();
  const name = [first ? `${first[0]}.` : null, last].filter(Boolean).join(' ');
  const extra = Math.max(0, travellerCount(item.travellers) - 1);
  const base = name || item.itineraryCompanyName || '—';
  return extra > 0 ? `${base} +${extra}` : base;
};

const operatorViews = (raw: unknown): DashboardOperatorView[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, index) => {
      const op = (entry ?? {}) as {
        id?: string;
        name?: string;
        title?: string;
      };
      const name = (op.name ?? op.title ?? '').trim();
      if (!name) return null;
      return { id: op.id ?? String(index), initials: initialsOf(name), name };
    })
    .filter((op): op is DashboardOperatorView => op !== null);
};

const ownerLabel = (item: OperationItem): string => {
  const account = [
    item.itineraryAccountFirstName,
    item.itineraryAccountLastName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (account) return account;
  return item.itineraryCompanyName?.trim() || '—';
};

// Maps a raw operations row to the flat view model the bespoke dashboard table
// renders. Keeps all jsonb/date parsing out of the presentational components.
export const toDashboardRow = (item: OperationItem): DashboardRowView => {
  const start = item.startTime ? toUtcDate(item.startTime) : null;
  return {
    id: item.id ?? '',
    dateLabel: start ? format(start, 'MMM dd') : '—',
    timeLabel: start ? format(start, 'HH:mm') : '',
    service: displayTitle(item),
    category: item.contentCategory ?? item.serviceType ?? '',
    itineraryId: item.itineraryId,
    itineraryRef: item.itineraryRef ?? '',
    traveller: travellerLabel(item),
    owner: ownerLabel(item),
    location: [item.city, item.country].filter(Boolean).join(', '),
    operators: operatorViews(item.tourOperators),
    statusId: item.operationStatusId,
    statusTitle: item.operationStatusTitle ?? '',
  };
};
